import {
  videoLabel,
  type VideoMetricRow,
  type VideoSelection,
} from "@meta-video/contracts";
import { Button, Group, Stack, Text } from "@mantine/core";

import { StatusBadge } from "../video/StatusBadge.js";
import type { VideoApi } from "../video/api.js";
import { VideoMetricSummary } from "../video/VideoMetricSummary.js";
import { VideoPlayer } from "../video/VideoPlayer.js";
import { VideoIdentityCell } from "./VideoIdentityCell.js";

export interface SelectedVideoPanelProps {
  readonly api?: VideoApi;
  readonly collapsed?: boolean;
  readonly disabled?: boolean;
  readonly id: string;
  /** The report is refetching, so an absent row is not yet evidence of removal. */
  readonly loading?: boolean;
  readonly onAccessChanged?: () => void;
  readonly onAddToComparison?: (selection: VideoSelection) => void;
  readonly onCollapsedChange?: (collapsed: boolean) => void;
  readonly onSessionExpired?: () => void;
  readonly onInspect?: () => void;
  readonly row: VideoMetricRow | null;
  readonly selection: VideoSelection | null;
  readonly videoMetricCoverageThreshold: number;
}

export function SelectedVideoPanel({
  api,
  collapsed = false,
  disabled = false,
  id,
  loading = false,
  onAccessChanged,
  onAddToComparison,
  onCollapsedChange,
  onSessionExpired,
  onInspect,
  row,
  selection,
  videoMetricCoverageThreshold,
}: SelectedVideoPanelProps) {
  const exactRow = selection !== null
    && row?.brandId === selection.brandId
    && row.videoId === selection.videoId
    ? row
    : null;
  const key = selection === null
    ? null
    : `${selection.brandId}\0${selection.videoId}`;
  const headingId = `${id}-heading`;
  const contentId = `${id}-content`;
  const label = exactRow === null ? null : videoLabel(exactRow).primary;

  return (
    <section
      aria-disabled={disabled ? "true" : undefined}
      aria-labelledby={headingId}
      className={`selected-video-panel${collapsed ? " selected-video-panel--collapsed" : ""}`}
      data-video-key={key ?? undefined}
      id={id}
      inert={disabled ? true : undefined}
      tabIndex={-1}
    >
      <Stack gap="sm">
        <Group className="selected-video-panel__header" justify="space-between" wrap="nowrap">
          <h2 className={collapsed ? "visually-hidden" : undefined} id={headingId}>
            Selected video
          </h2>
          {onCollapsedChange ? (
            <Button
              aria-controls={contentId}
              aria-expanded={!collapsed}
              aria-label={`${collapsed ? "Expand" : "Collapse"} selected video`}
              className="selected-video-panel__collapse"
              disabled={disabled}
              onClick={() => onCollapsedChange(!collapsed)}
              size="compact-xs"
              title={`${collapsed ? "Expand" : "Collapse"} selected video`}
              variant="subtle"
            >
              {collapsed ? "‹" : "›"}
            </Button>
          ) : null}
        </Group>
        <div hidden={collapsed} id={contentId}>
          <Stack gap="sm">
            {selection === null ? (
              <Text aria-live="polite" c="dimmed" role="status" size="sm">
                Select a video row to inspect its identity and playback.
              </Text>
            ) : exactRow === null && loading ? (
              // The row lookup only covers the page currently in hand. While a
              // replacement report is in flight that page is stale, so absence
              // is not yet evidence the video was removed.
              <Text aria-live="polite" c="dimmed" role="status" size="sm">
                Loading the selected video…
              </Text>
            ) : exactRow === null ? (
              <Text aria-live="polite" c="dimmed" role="status" size="sm">
                The selected video is no longer available in these report results. Choose another row or return to the report context where it appeared.
              </Text>
            ) : (
              <>
                <span aria-live="polite" className="visually-hidden" role="status">
                  Selected video: {label}
                </span>
                <VideoIdentityCell identity={exactRow} />
                <Group gap="xs" wrap="wrap">
                  {onInspect ? (
                    <Button
                      aria-label={`Inspect ${label} details`}
                      disabled={disabled}
                      onClick={onInspect}
                      size="compact-sm"
                      variant="default"
                    >
                      Inspect details
                    </Button>
                  ) : null}
                  {onAddToComparison ? (
                    <Button
                      onClick={() => onAddToComparison(selection)}
                      disabled={disabled}
                      size="compact-sm"
                      variant="light"
                    >
                      Add to comparison
                    </Button>
                  ) : null}
                </Group>
                {exactRow.playStatus === "hosted" ? (
                  <VideoPlayer
                    api={api}
                    autoPrepare
                    key={key!}
                    label={label!}
                    onAccessChanged={onAccessChanged}
                    onSessionExpired={onSessionExpired}
                    selection={selection}
                    surface="report"
                  />
                ) : <StatusBadge status={exactRow.playStatus} />}
                <VideoMetricSummary
                  row={exactRow}
                  videoMetricCoverageThreshold={videoMetricCoverageThreshold}
                />
              </>
            )}
          </Stack>
        </div>
      </Stack>
    </section>
  );
}
